import { EGAEventName, useGAHelpers } from 'lib/hooks/useGAHelper';
import React from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { ApplicationState } from 'store/store';

function parseAudienceIdsFromSearch(search: string): string[] {
    const params = new URLSearchParams(search);
    const raw = params.get('audienceIds');
    return raw ? raw.split(',').filter(Boolean).slice(0, 4) : [];
}

export function useAudienceAnalyticsAudiencePicker(colors: [string, string, string, string, string]) {
    const { logUserEvent } = useGAHelpers();
    const { audiences } = useSelector((state: ApplicationState) => state.audiences);
    const location = useLocation();
    const navigate = useNavigate();

    const audienceOptions = React.useMemo(() => {
        if (!audiences) {
            return [];
        }
        return audiences.map(item => ({ value: item._id, label: `${item.title} [${item._id}]` }));
    }, [audiences]);
    const [audienceSelectOpen, setAudienceSelectOpen] = React.useState(false);
    const [audiencesToCompare, setAudiencesToCompare] = React.useState<string[]>(() =>
        parseAudienceIdsFromSearch(location.search)
    );

    React.useEffect(() => {
        const search = audiencesToCompare.length > 0 ? `audienceIds=${audiencesToCompare.join(',')}` : '';
        navigate({ pathname: location.pathname, search }, { replace: true });
    }, [audiencesToCompare, location.pathname, navigate]);

    const audiencesConfig = React.useMemo(() => {
        const newRecord: Record<string, { title: string; color: string }> = {};
        audiencesToCompare?.forEach((audienceId, index) => {
            const audience = audiences?.find(audience => audience._id === audienceId);
            newRecord[audienceId] = {
                title: audience?.title ?? audienceId,
                color: colors[index + 1]
            };
        });
        return newRecord;
    }, [colors, audiencesToCompare, audiences]);

    const handleAudienceSelectOpen = React.useCallback(() => {
        setAudienceSelectOpen(true);
    }, []);
    const handleAudienceSelectClose = React.useCallback(() => {
        setAudienceSelectOpen(false);
    }, []);
    const handleAudienceChangeSubmit = React.useCallback(
        (values: string[]) => {
            setAudiencesToCompare(values);
            setAudienceSelectOpen(false);
            if (values.length > 0) {
                logUserEvent(EGAEventName.AudienceAnalyticsAudiencesSelected);
            }
        },
        [logUserEvent]
    );
    return {
        audienceOptions,
        audienceSelectOpen,
        audiencesToCompare,
        audiencesConfig,
        handleAudienceSelectOpen,
        handleAudienceSelectClose,
        handleAudienceChangeSubmit
    };
}
